import { MaterialArea, MaterialStatus } from "@fusion-advantage/backcompat"
import { sqlQuery, sqlRun } from "./ldb"
import { bulkInsert } from "./bulkInsert"
import { CloudToDeviceOfflineTableMaterialInventoryBody } from "../redux/syncthunk/ZCloudToDevice"
import { err, ok, Result } from "neverthrow"
import { FusionError, serializeFusionError } from '@fusion-advantage/backcompat'
import { selectAllText } from "./LdbBrowser"

export const ldbMaterialInventoryBulkInsert = async (body: CloudToDeviceOfflineTableMaterialInventoryBody): Promise<Result<void, FusionError>> => {
	const rDelete = await sqlRun(`
		delete from materialInventory
	`)
	if(rDelete.isErr()) {
		return err(serializeFusionError('ldbMaterialInventoryBulkInsert', rDelete.error))
	}
	let params = []
	for (let m of body.Materials) {
		params.push(m.Area, m.Id, m.Type, m.Status)
	}
	const rInsert = await bulkInsert('materialInventory', params, 4)
	if(rInsert.isErr()) {
		return err(serializeFusionError('ldbMaterialInventoryBulkInsert', rInsert.error))
	}
	// We decided to delete local kit claims on the next offline update
	// after 24 full hours had passed.  This would give Dan some time to do a
	// nightly update of mart data from submitted encounters and update his
	// master inventory, which is what the offline feed is generated from that
	// is bulk inserted here.
	// I pointed out that started encounters could remain unsubmitted for
	// longer than 24 hours, but his thought is that all encounters _should_
	// be submitted within 24 hours of start (and that virtually all are),
	// and any that last longer than that are the "1%" case, which we are not
	// aiming for.  The point of the feature truly is to be a sanity check
	// to prevent clinicians from doing something obviously wrong.
	const rClean = await sqlRun(`
		delete from materialClaim
		 where claimedAt < (unixepoch() - 86400)
	`)
	if(rClean.isErr()) {
		return err(serializeFusionError('ldbMaterialInventoryBulkInsert', rClean.error))
	}
	return ok()
}

export interface MaterialStatusRow {
	area: MaterialArea
	id: string
	type: string|null
	status: string
	encounterGuid: string|null
}

export const selectMaterialStatus = async (area: MaterialArea, id: string, type: string, excludeEncounterGuid?: string): Promise<Result<MaterialStatus, FusionError>> => {
	let r = await sqlQuery<MaterialStatusRow>(
		`
			select i.area, i.id, i.type, i.status, c.encounterGuid
			  from materialInventory i
			  full outer join materialClaim c
			    on (i.area = c.area and i.id = c.id)
			 where (i.area = ? and i.id = ?)
			    or (c.area = ? and c.id = ?)
		`,
		[
			area, id,
			area, id,
		],
	)
	if(r.isErr()) {
		return err(serializeFusionError('selectMaterialStatus', r.error))
	}
	const results = r.value
	if(results.length < 1) {
		return ok(MaterialStatus.Unknown)
	}
	if(results.length > 1) {
		return err(serializeFusionError('selectMaterialStatus', 'returned more than 1 row'))
	}
	const result = results[0]
	if(result.encounterGuid !== null && result.encounterGuid !== excludeEncounterGuid) {
		return ok(MaterialStatus.Used)
	}
	// Per the outer join above,
	// Type will be null if the material is claimed & NOT in inventory,
	// because the API always sends back a not-null value for Type.
	// Dan has asked us not to consider Type when matching local use claims
	// because the inventory key in his design does not include it;
	// his key is only Area+ID.
	// The reason we have Type available here is to check that the clinician
	// has scanned the right type of barcode in for the lab in question.
	// See also code comments in /api/v2/offline/table/material-inventory
	if(result.type !== null && result.type !== type) {
		return ok(MaterialStatus.WrongType)
	}
	let status = MaterialStatus[result.status as keyof typeof MaterialStatus]
	return status ? ok(status) : ok(MaterialStatus.Unknown)
}

// this is for the debug screen
// export const selectAllMaterialStatus = async (): Promise<Result<MaterialStatusRow[], FusionError>> => {
// 	let r = await sqlQuery<MaterialStatusRow>(
// 		`
// 			select coalesce(i.area, c.area) as area,
// 			       coalesce(i.id, c.id) as id,
// 			       i.type,
// 			       i.status,
// 			       c.encounterGuid
// 			  from materialInventory i
// 			  full outer join materialClaim c
// 			    on (i.area = c.area and i.id = c.id)
// 		`,
// 	)
// 	if(r.isErr()) {
// 		return err(serializeFusionError('selectAllMaterialStatus', r.error))
// 	}
// 	return ok(r.value)
// }

export const selectAllMaterialInventoryText = async (): Promise<string> => {
	return selectAllText('materialInventory', ['area', 'id', 'type', 'status'])
}

export const selectAllMaterialClaimText = async (): Promise<string> => {
	return selectAllText('materialClaim', ['area', 'id', 'encounterGuid', 'claimedAt'])
}

export const replaceMaterialClaims = async (encounterGuid: string, keys: {area: MaterialArea, id: string}[], claimedAt?: number): Promise<Result<void, FusionError>> => {
	let r = await sqlRun(
		`
			delete from materialClaim
			 where encounterGuid = ?
		`,
		[
			encounterGuid,
		],
	)
	if(r.isErr()) {
		return err(serializeFusionError('replaceMaterialClaims', r.error))
	}
	for(let key of keys) {
		r = await sqlRun(
			`
				insert into materialClaim
					(area, id, encounterGuid, claimedAt)
				values
					(?, ?, ?, coalesce(?, unixepoch()))
			`,
			[
				key.area, key.id, encounterGuid, (claimedAt ?? null),
			],
		)
		if(r.isErr()) {
			return err(serializeFusionError('replaceMaterialClaims', r.error))
		}
	}
	return ok()
}

interface MaterialClaim {
	area: MaterialArea
	id: string
	encounterGuid: string
	claimedAt: number
}

export const selectMaterialClaim = async (area: MaterialArea, id: string): Promise<Result<MaterialClaim|null, FusionError>> => {
	let r = await sqlQuery<MaterialClaim>(
		`
			select area, id, encounterGuid, claimedAt
			  from materialClaim 
			 where area = ?
			   and id = ?
		`,
		[
			area, id,
		],
	)
	if(r.isErr()) {
		return err(serializeFusionError('selectMaterialClaim', r.error))
	}
	return r.value[0] ? ok(r.value[0]) : ok(null)
}
